import {
  ArrowLeftOutlined,
  EditOutlined,
  FilePdfOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Alert, Button, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useGetProductQuery } from '@/modules/products/productsApi';
import type { Product, ProductVariant } from '@/types/product';
import { getMediaImageUrl, getSamplePdfUrl } from '@/utils/media';
import {
  formatProductPriceRange,
  formatProductSalePriceRange,
} from '@/utils/product';
import { buildVariantKey } from '@/utils/productVariants';
import { calculateDiscountFromPrices } from '@/utils/productPricing';
import { ucfirst } from '@/utils/string';
import styles from './ProductView.module.scss';

interface AttributeGroup {
  attributeId: string;
  attributeName: string;
  options: Array<{ valueId: string; name: string; colorCode?: string }>;
}

function formatInr(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function getGalleryImages(product: Product, variant?: ProductVariant | null): string[] {
  const urls: string[] = [];

  const push = (url?: string) => {
    const resolved = getMediaImageUrl(url);
    if (resolved && !urls.includes(resolved)) urls.push(resolved);
  };

  if (variant?.image) {
    push(variant.image.imageLarge ?? variant.image.imageSmall);
  }

  push(product.thumbnail?.imageLarge ?? product.thumbnail?.imageSmall);
  product.images?.forEach((image) => push(image.imageLarge ?? image.imageSmall));

  return urls;
}

function buildAttributeGroups(product: Product): AttributeGroup[] {
  const variants = product.variants?.filter((variant) => variant.isActive) ?? [];
  const groups = new Map<string, AttributeGroup>();

  variants.forEach((variant) => {
    variant.attributeValues?.forEach((value) => {
      if (!groups.has(value.attributeId)) {
        const index = product.attributeIds.indexOf(value.attributeId);
        const attributeName =
          product.attributeNames?.[index] ?? value.name.split(' ')[0] ?? 'Format';

        groups.set(value.attributeId, {
          attributeId: value.attributeId,
          attributeName,
          options: [],
        });
      }

      const group = groups.get(value.attributeId);
      if (!group) return;

      if (!group.options.some((option) => option.valueId === value.id)) {
        group.options.push({
          valueId: value.id,
          name: value.name,
          colorCode: value.colorCode,
        });
      }
    });
  });

  return [...groups.values()];
}

function getDefaultSelection(product: Product): Record<string, string> {
  const variant = product.variants?.find((item) => item.isActive) ?? product.variants?.[0];
  if (!variant) return {};

  const selection: Record<string, string> = {};
  variant.attributeValues?.forEach((value) => {
    selection[value.attributeId] = value.id;
  });
  return selection;
}

function findVariant(product: Product, selection: Record<string, string>): ProductVariant | null {
  const valueIds = Object.values(selection);
  if (!valueIds.length) return null;

  const key = buildVariantKey(valueIds);
  return (
    product.variants?.find(
      (variant) => buildVariantKey(variant.attributeValueIds) === key && variant.isActive,
    ) ?? null
  );
}

function getProductDiscount(product: Product, variant?: ProductVariant | null): number | undefined {
  if (product.productType === 'variable' && variant) {
    return variant.discount ?? calculateDiscountFromPrices(variant.regularPrice, variant.salePrice);
  }

  return product.discount ?? calculateDiscountFromPrices(product.regularPrice, product.salePrice);
}

function FactRow({ label, value }: { label: string; value?: string }) {
  if (!value?.trim()) return null;

  return (
    <div className={styles.factRow}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

function PublicationSection({ title, html }: { title: string; html?: string }) {
  if (!html?.trim()) return null;

  return (
    <article className={styles.articleBlock}>
      <h3 className={styles.articleTitle}>{title}</h3>
      <div className={styles.richText} dangerouslySetInnerHTML={{ __html: html }} />
    </article>
  );
}

function SpecificationsPanel({ product }: { product: Product }) {
  const rows = [
    { label: 'Format', value: product.formatDetails },
    { label: 'Pages', value: product.pageCount ?? '' },
    { label: 'Edition', value: product.edition },
    { label: 'First published', value: product.firstPublished },
    { label: 'Language', value: product.language },
    { label: 'ISBN-13', value: product.isbn },
    { label: 'Imprint', value: product.imprint },
    { label: 'Weight (Approx.)', value: product.weight },
    { label: 'Dimensions', value: product.dimensions },
  ].filter((row) => row.value?.trim());

  if (!rows.length) return null;

  return (
    <aside className={styles.specPanel}>
      <h4 className={styles.panelHeading}>Specifications</h4>
      <dl className={styles.specTable}>
        {rows.map((row) => (
          <div key={row.label} className={styles.specLine}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

function CitationPanel({ product }: { product: Product }) {
  const hasCitation = product.apaCitation?.trim() || product.chicagoCitation?.trim();
  if (!hasCitation) return null;

  return (
    <aside className={styles.citePanel}>
      <h4 className={styles.panelHeading}>Cite this book</h4>
      {product.apaCitation?.trim() ? (
        <div className={styles.citeBox}>
          <p className={styles.citeStyle}>APA · 7th ed.</p>
          <p className={styles.citeBody}>{product.apaCitation}</p>
        </div>
      ) : null}
      {product.chicagoCitation?.trim() ? (
        <div className={styles.citeBox}>
          <p className={styles.citeStyle}>Chicago · 17th ed.</p>
          <p className={styles.citeBody}>{product.chicagoCitation}</p>
        </div>
      ) : null}
    </aside>
  );
}

export default function ProductView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: product,
    isLoading,
    isFetching,
  } = useGetProductQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  useGlobalLoader(isLoading || isFetching);

  const [slideIndex, setSlideIndex] = useState(0);
  const [selection, setSelection] = useState<Record<string, string>>({});

  const attributeGroups = useMemo(() => (product ? buildAttributeGroups(product) : []), [product]);

  const effectiveSelection = useMemo(() => {
    if (!product) return {};
    if (Object.keys(selection).length) return selection;
    return getDefaultSelection(product);
  }, [product, selection]);

  const selectedVariant = useMemo(
    () => (product?.productType === 'variable' ? findVariant(product, effectiveSelection) : null),
    [effectiveSelection, product],
  );

  const galleryImages = useMemo(
    () => (product ? getGalleryImages(product, selectedVariant) : []),
    [product, selectedVariant],
  );

  const displayPrice = useMemo(() => {
    if (!product) return { sale: null as string | null, regular: '—' };

    if (product.productType === 'variable' && selectedVariant) {
      const regular = formatInr(selectedVariant.regularPrice);
      const sale =
        selectedVariant.salePrice != null &&
        selectedVariant.salePrice < selectedVariant.regularPrice
          ? formatInr(selectedVariant.salePrice)
          : null;
      return { sale, regular };
    }

    return {
      sale: formatProductSalePriceRange(product),
      regular: formatProductPriceRange(product),
    };
  }, [product, selectedVariant]);

  const displayDiscount = useMemo(() => {
    if (!product) return undefined;
    return getProductDiscount(product, selectedVariant);
  }, [product, selectedVariant]);

  const seriesBadge = useMemo(() => {
    if (!product) return '';
    const parts = [
      product.seriesName,
      product.seriesNumber ? `Series · No. ${product.seriesNumber}` : '',
    ].filter(Boolean);
    return parts.join(' · ');
  }, [product]);

  const formatLine = useMemo(() => {
    if (!product) return '';
    return [product.formatDetails, product.pageCount].filter(Boolean).join(' · ');
  }, [product]);

  const hasSpecs = useMemo(() => {
    if (!product) return false;
    return Boolean(
      product.formatDetails ||
        product.pageCount ||
        product.edition ||
        product.firstPublished ||
        product.language ||
        product.isbn ||
        product.imprint ||
        product.weight ||
        product.dimensions,
    );
  }, [product]);

  const hasCitation = useMemo(
    () => Boolean(product?.apaCitation?.trim() || product?.chicagoCitation?.trim()),
    [product],
  );

  const hasSidebar = hasSpecs || hasCitation;

  useEffect(() => {
    setSlideIndex(0);
  }, [galleryImages, selectedVariant?.id]);

  if (!product && !isLoading) {
    return (
      <div className={styles.page}>
        <Alert type="error" message="Publication not found." showIcon />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.PRODUCTS)}>
              Back
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/products/${id}/edit`)}
            >
              Edit publication
            </Button>
          </>
        }
      />

      {product ? (
        <div className={styles.factbook}>
          <section className={styles.heroBand}>
            <div className={styles.coverColumn}>
              <div className={styles.coverFrame}>
                {galleryImages.length ? (
                  <img
                    src={galleryImages[slideIndex]}
                    alt={product.name}
                    className={styles.coverImage}
                  />
                ) : (
                  <div className={styles.coverPlaceholder}>No cover image</div>
                )}

                {galleryImages.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className={`${styles.coverNav} ${styles.coverNavPrev}`}
                      aria-label="Previous image"
                      onClick={() =>
                        setSlideIndex(
                          (current) => (current - 1 + galleryImages.length) % galleryImages.length,
                        )
                      }
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      type="button"
                      className={`${styles.coverNav} ${styles.coverNavNext}`}
                      aria-label="Next image"
                      onClick={() =>
                        setSlideIndex((current) => (current + 1) % galleryImages.length)
                      }
                    >
                      <RightOutlined />
                    </button>
                  </>
                ) : null}
              </div>

              {galleryImages.length > 1 ? (
                <div className={styles.thumbStrip}>
                  {galleryImages.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      className={`${styles.thumbBtn} ${index === slideIndex ? styles.thumbBtnActive : ''}`}
                      aria-label={`View image ${index + 1}`}
                      onClick={() => setSlideIndex(index)}
                    >
                      <img src={image} alt="" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={styles.buyColumn}>
              <div className={styles.buyTop}>
                {seriesBadge ? <p className={styles.eyebrow}>{seriesBadge}</p> : null}
                <h1 className={styles.bookTitle}>{ucfirst(product.name)}</h1>
                <Tag className={styles.statusPill} color={product.isActive ? 'blue' : 'default'}>
                  {product.isActive ? 'Published' : 'Draft'}
                </Tag>
              </div>

              {product.productType === 'variable' && attributeGroups.length ? (
                <div className={styles.formatBlock}>
                  {attributeGroups.map((group) => (
                    <div key={group.attributeId} className={styles.formatGroup}>
                      <div className={styles.formatPills}>
                        {group.options.map((option) => {
                          const isSelected = effectiveSelection[group.attributeId] === option.valueId;
                          return (
                            <button
                              key={option.valueId}
                              type="button"
                              className={`${styles.formatPill} ${isSelected ? styles.formatPillActive : ''}`}
                              onClick={() =>
                                setSelection((current) => ({
                                  ...current,
                                  [group.attributeId]: option.valueId,
                                }))
                              }
                            >
                              {option.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {product.shortDescription ? (
                <p className={styles.lead}>
                  <span className={styles.leadLabel}>Description:</span> {product.shortDescription}
                </p>
              ) : null}

              <div className={styles.factList}>
                <FactRow label="Editor" value={product.editor} />
                <FactRow label="Edition" value={product.edition} />
                <FactRow label="ISBN" value={product.isbn} />
                <FactRow label="Expected delivery time" value={product.deliveryTime} />
              </div>

              <div className={styles.pricePanel}>
                <div className={styles.priceMain}>
                  {displayPrice.sale ? (
                    <>
                      <span className={styles.priceNow}>{displayPrice.sale}</span>
                      <span className={styles.priceWas}>{displayPrice.regular}</span>
                    </>
                  ) : (
                    <span className={styles.priceNow}>{displayPrice.regular}</span>
                  )}
                  {displayDiscount ? (
                    <span className={styles.saveTag}>Save {displayDiscount}%</span>
                  ) : null}
                </div>
                {formatLine ? <p className={styles.formatLine}>{formatLine}</p> : null}
                <p className={styles.priceNote}>Incl. GST · Free shipping in India</p>
              </div>

              {product.samplePdfUrl ? (
                <a
                  href={getSamplePdfUrl(product.samplePdfUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.sampleAction}
                >
                  <FilePdfOutlined />
                  View sample PDF
                  <span aria-hidden>→</span>
                </a>
              ) : null}

              {(product.categoryNames?.length ?? 0) > 0 ? (
                <div className={styles.chipRow}>
                  {product.categoryNames?.map((name) => (
                    <span key={name} className={styles.chip}>
                      {ucfirst(name)}
                    </span>
                  ))}
                </div>
              ) : null}

              {product.sku ? <p className={styles.skuLine}>SKU {product.sku}</p> : null}
            </div>
          </section>

          <section className={`${styles.bodyBand} ${hasSidebar ? '' : styles.bodyBandWide}`}>
            <div className={styles.readingColumn}>
              <PublicationSection title="About this publication" html={product.description} />
              {/*
                These three sections are sourced from the selected imprint attribute value
                on the public product detail page.
              */}
              <PublicationSection title="Institutional & bulk" html={product.returnExchangePolicy} />
            </div>

            {hasSidebar ? (
              <div className={styles.sidebarColumn}>
                {hasSpecs ? <SpecificationsPanel product={product} /> : null}
                {hasCitation ? <CitationPanel product={product} /> : null}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
}
